import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Setting } from "../../models/Setting";
import { upsertSettings } from "../../repositories/settingRepository";

type Props = {
	settingSelected: Setting | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const CreateSetting = ({
	settingSelected,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const { control, handleSubmit, reset } = useForm<Setting>({
		defaultValues: settingSelected ? settingSelected : INITIAL_VALUES,
	});

	const updateSetting = useMutation({
		mutationFn: upsertSettings,
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<Setting> = async (data) => {
		setIsSubmitLoading(true);
		await updateSetting.mutate({
			oldName: "",
			newName: data.SettingName,
			description: data.Description,
			value: data.Value,
		});
	};

	const handleClose = (
		event?: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	useEffect(() => {
		if (show) {
			reset();
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`New Setting${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Grid2 container>
				<Grid2>
					<MesfModal
						title="NEW SETTING"
						open={show}
						handleClose={() => onHide(false)}
						id="create-setting-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content>
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Controller
											name="SettingName"
											control={control}
											rules={{ required: "Name is required" }}
											render={({ field, fieldState: { error } }) => (
												<TextField
													{...field}
													label="Name"
													error={!!error}
													helperText={error ? error.message : null}
													autoComplete="off"
												/>
											)}
										/>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Controller
											name="Value"
											control={control}
											rules={{
												required: "Value is required",
											}}
											render={({ field, fieldState: { error } }) => (
												<TextField
													{...field}
													label="Value"
													error={!!error}
													helperText={error ? error.message : null}
													autoComplete="off"
												/>
											)}
										/>
									</Grid2>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="Description"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													size="medium"
													maxRows={5}
													label="Description"
													multiline
													autoComplete="off"
													value={value}
													onChange={(evt) => {
														if (evt.target.value.length <= 200) onChange(evt);
													}}
												/>
											)}
										/>
									</Grid2>
								</Grid2>
							</MesfModal.Content>
							<MesfModal.Actions>
								<Grid2 container spacing={2} justifyContent="flex-end">
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid2>
								</Grid2>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Setting"
			/>
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The setting was created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
